import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Chip,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  ListItemIcon,
  Container,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as MuiIcons from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import nodeAxiosFirebase from '../utils/nodeAxiosFirebase';
import BlueprintEditor from '../components/BlueprintEditor';
import {
  getBlueprintDetails,
  updateBlueprint,
  createBlueprint,
  getBlueprintCustomerDetails,
} from '../services/firestoreService';

function CustomerDetailsPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { customers } = useSelector((state) => state.customers);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blueprintData, setBlueprintData] = useState({
    structures: [],
    modules: [],
    extensions: [],
  });
  const [bluePrintId, setBluePrintId] = useState(null);
  const [isBlueprintEditorOpen, setIsBlueprintEditorOpen] = useState(false);
  const [isSavingBlueprint, setIsSavingBlueprint] = useState(false);
  const [blueprintError, setBlueprintError] = useState(null);
  const [existingBlueprintId, setExistingBlueprintId] = useState(null);

  const customer = customers.find((c) => c.id === customerId);

  const fetchData = async () => {
    if (!customerId) return;

    try {
      setLoading(true);

      const response = await nodeAxiosFirebase({
        t,
        method: 'POST',
        url: 'master',
        body: {
          businessId: customerId,
          action: 'details',
        },
      });
      setLoading(false);
      if (!response) {
        throw new Error('No response received from server');
      }

      if (response.errorData) {
        throw new Error(
          response.errorData.message || 'Server returned an error'
        );
      }

      setCustomerDetails(response);
      setError(null);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
      setError(err.message || 'Failed to load customer details');
      setCustomerDetails(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [customerId]);

  useEffect(() => {
    if (customer) {
      document.title = `${customer.name} - BlueLink`;
    } else {
      document.title = 'Customer Details - BlueLink';
    }
  }, [customer]);

  useEffect(() => {
    if (customerDetails) {
      fetchBlueprintData();
    }
  }, [customerDetails]);

  const fetchBlueprintData = async () => {
    try {
      setBlueprintError(null);

      const masterData = customerDetails;
      const structures = masterData?.structures || [];
      const modules = masterData?.modules || [];

      const blueprint = await getBlueprintCustomerDetails(customerId);
      setBluePrintId(blueprint?.id);

      if (blueprint) {
        setExistingBlueprintId(blueprint.id);

        const mergedStructures = structures.map((masterStructure) => {
          const blueprintStructure = blueprint.structures?.find(
            (bs) => bs.id === masterStructure.id
          );

          if (blueprintStructure) {
            return {
              ...masterStructure,
              ...blueprintStructure,
              name_fr: blueprintStructure.name_fr || masterStructure.name_fr,
              name_en: blueprintStructure.name_en || masterStructure.name_en,
              element: blueprintStructure.element || masterStructure.element,
              fields: blueprintStructure.fields || masterStructure.fields,
              tabs:
                blueprintStructure.tabs?.map((masterTab, tabIndex) => {
                  const blueprintTab = blueprintStructure.tabs?.[tabIndex];
                  if (blueprintTab) {
                    const mergedBlocks =
                      masterTab.blocks?.map((masterBlock) => {
                        const blueprintBlock = blueprintTab.blocks?.find(
                          (bb) => bb.i === masterBlock.i
                        );
                        return blueprintBlock
                          ? {
                              ...masterBlock,
                              ...blueprintBlock,
                              name_fr:
                                blueprintBlock.name_fr || masterBlock.name_fr,
                              name_en:
                                blueprintBlock.name_en || masterBlock.name_en,
                              type: blueprintBlock.type || masterBlock.type,
                              contentType:
                                blueprintBlock.contentType ||
                                masterBlock.contentType,
                              price:
                                blueprintBlock.price || masterBlock.price || 0,
                              structureId:
                                blueprintBlock.structureId ||
                                masterBlock.structureId,
                              color: blueprintBlock.color || masterBlock.color,
                              match: blueprintBlock.match || masterBlock.match,
                              filter:
                                blueprintBlock.filter || masterBlock.filter,
                              createBtn:
                                blueprintBlock.createBtn ||
                                masterBlock.createBtn,
                              editBtn:
                                blueprintBlock.editBtn || masterBlock.editBtn,
                              refreshBtn:
                                blueprintBlock.refreshBtn ||
                                masterBlock.refreshBtn,
                              realtime:
                                blueprintBlock.realtime || masterBlock.realtime,
                              icon: blueprintBlock.icon || masterBlock.icon,
                              infos: blueprintBlock.infos || masterBlock.infos,
                              groups:
                                blueprintBlock.groups || masterBlock.groups,
                              x: blueprintBlock.x || masterBlock.x,
                              y: blueprintBlock.y || masterBlock.y,
                              w: blueprintBlock.w || masterBlock.w,
                              h: blueprintBlock.h || masterBlock.h,
                              i: blueprintBlock.i || masterBlock.i,
                            }
                          : masterBlock;
                      }) || [];

                    // Add any new blocks from blueprint that don't exist in master
                    const newBlueprintBlocks =
                      blueprintTab.blocks?.filter(
                        (bb) => !masterTab.blocks?.some((mb) => mb.i === bb.i)
                      ) || [];

                    return {
                      ...masterTab,
                      ...blueprintTab,
                      blocks: [...mergedBlocks, ...newBlueprintBlocks],
                    };
                  }
                  return masterTab;
                }) ||
                blueprintStructure.tabs ||
                [],
            };
          }
          return masterStructure;
        });

        // Merge modules while preserving specific fields
        const mergedModules = modules?.map((masterModule) => {
          const blueprintModule = blueprint.modules?.find(
            (bm) => bm.id === masterModule.id
          );
          return blueprintModule
            ? {
                ...masterModule,
                ...blueprintModule,
                name_fr: blueprintModule.name_fr || masterModule.name_fr,
                name_en: blueprintModule.name_en || masterModule.name_en,
                description:
                  blueprintModule.description || masterModule.description,
              }
            : masterModule;
        });

        setBlueprintData({
          structures: blueprint?.structures || [],
          modules: blueprint?.modules || [],
          extensions: blueprint.extensions || [],
        });
      } else {
        setBlueprintData({
          structures,
          modules,
          extensions: [],
        });
      }
    } catch (error) {
      console.error('Error fetching blueprint data:', error);
      setBlueprintError(error.message);
    }
  };

  const handleCreateBlueprint = () => {
    setIsBlueprintEditorOpen(true);
  };

  const handleSaveBlueprint = async (newData) => {
    try {
      setIsSavingBlueprint(true);
      setBlueprintError(null);

      const cleanObject = (obj) => {
        if (!obj) return {};
        return Object.entries(obj).reduce((acc, [key, value]) => {
          if (value !== undefined) {
            if (Array.isArray(value)) {
              if (value.length > 0 && typeof value[0] === 'string') {
                acc[key] = value;
              } else {
                acc[key] = value.map((item) => cleanObject(item));
              }
            } else if (typeof value === 'object' && value !== null) {
              acc[key] = cleanObject(value);
            } else {
              acc[key] = value;
            }
          }
          return acc;
        }, {});
      };

      const blueprintData = {
        ownerId: customerId,
        name: customer?.name || '',
        phone: customer?.phone || '',
        email: customer?.email || '',
        address: customer?.address || '',
        mainColor: customer?.mainColor || '',
        secColor: customer?.secColor || '',
        structures: newData?.structures?.map((structure) => ({
          attribute1: structure?.attribute1 || '',
          attribute2: structure?.attribute2 || '',
          attribute3: structure?.attribute3 || '',
          attribute4: structure?.attribute4 || '',
          name_fr: structure?.name_fr || '',
          name_en: structure?.name_en || '',
          groups: structure?.groups || [],
          id: structure?.id || '',
          element: structure?.element || '',
          fields: structure?.fields || [],
          icon: structure?.icon || '',
          collectionField: structure?.collectionField || '',
          paths: structure?.paths || [],
          tabs: structure?.tabs?.map((tab) => ({
            name_fr: tab?.name_fr || '',
            name_en: tab?.name_en || '',
            groups: tab?.groups || [],
            blocks: tab?.blocks?.map((block) =>
              cleanObject({
                name_fr: block?.name_fr || '',
                name_en: block?.name_en || '',
                type: block?.type || '',
                contentType: block?.contentType || '',
                structureId: block?.structureId || structure?.id,
                color: block?.color || '',
                match: block?.match || '',
                filter: block?.filter || {},
                createBtn: block?.createBtn || false,
                editBtn: block?.editBtn || false,
                price: block?.price !== undefined ? block.price : 0,
                refreshBtn: block?.refreshBtn || false,
                realtime: block?.realtime || false,
                icon: block?.icon || '',
                infos: block?.infos || {},
                groups: block?.groups || [],
                x: block?.x || 0,
                y: block?.y || 0,
                w: block?.w || 0,
                h: block?.h || 0,
                i: block?.i || '',
              })
            ),
          })),
        })),
        modules: newData?.modules || [],
        extensions: newData?.extensions || [],
        updatedAt: new Date(),
      };

      // Clean the entire blueprint data before saving
      const cleanedBlueprintData = cleanObject(blueprintData);

      if (existingBlueprintId) {
        // Update existing blueprint
        await updateBlueprint(existingBlueprintId, cleanedBlueprintData);
      } else {
        // Create new blueprint
        const newBlueprintId = await createBlueprint(cleanedBlueprintData);
        setExistingBlueprintId(newBlueprintId);
      }

      setBlueprintData(newData);
      alert('Blueprint saved successfully!');
    } catch (error) {
      console.error('Error saving blueprint:', error);
      setBlueprintError(error.message);
      alert('Failed to save blueprint: ' + error.message);
    } finally {
      setIsSavingBlueprint(false);
    }
  };

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Customer not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/customers')}
          sx={{ mt: 2 }}
        >
          Back to Customers
        </Button>
      </Box>
    );
  }

  // Transform usage data for the chart if available
  const usageData =
    customerDetails?.usageData?.map((item) => ({
      date: new Date(item.timestamp).toLocaleDateString(),
      usage: item.value,
    })) || [];

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        overflowY: 'auto',
        boxSizing: 'border-box',
        margin: 0,
        position: 'absolute',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'flex-start',
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '1800px', marginLeft: '20px' }}>
        <Grid
          container
          spacing={3}
          sx={{
            width: '100%',
            margin: 0,
            '& > .MuiGrid-item': {
              padding: '12px',
              boxSizing: 'border-box',
            },
          }}
        >
          <Grid
            item
            xs={12}
            md={4}
            sx={{
              width: '20.333333%',
              flex: '0 0 20.333333%',
              position: 'relative',
            }}
          >
            <Box sx={{ position: 'fixed', left: 20, top: 20, width: '25%' }}>
              <Card sx={{ width: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <CardMedia
                      component="img"
                      sx={{
                        width: 100,
                        height: 100,
                        objectFit: 'contain',
                        borderRadius: 1,
                        backgroundColor: 'background.paper',
                        mb: 2,
                      }}
                      image={`https://storage.googleapis.com/business-logos-node/${customer.id}.png`}
                      alt={`${customer.name} logo`}
                    />
                    <Typography variant="h6">{customer.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ID: {customer.businessId || customer.id}
                    </Typography>
                    <Chip
                      label={customer.isActive ? t('active') : t('inactive')}
                      color={customer.isActive ? 'success' : 'error'}
                      sx={{ mt: 1 }}
                    />
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="Phone"
                        secondary={customer?.phone || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Address"
                        secondary={`${customer?.city || ''} ${
                          customer?.geo
                            ? `(${customer.geo._latitude}, ${customer.geo._longitude})`
                            : ''
                        }`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Owner"
                        secondary={customer?.ownerName || 'N/A'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Website"
                        secondary={customer?.website || 'N/A'}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
              {/* Account Information Section */}
              <Paper
                sx={{
                  p: 3,
                  mb: 3,
                  mt: 3,
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Account Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Plan
                    </Typography>
                    <Typography variant="body1">
                      {customer?.plan || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Renewal Date
                    </Typography>
                    <Typography variant="body1">
                      {customer?.billingReset
                        ? `${customer.billingReset}/${customer.frequency}`
                        : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Business Type
                    </Typography>
                    <Typography variant="body1">
                      {customer.businessType || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body1">
                      {customer?.currency?.toUpperCase() || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
            sx={{ width: '55.666667%', flex: '0 0 55.666667%' }}
          >
            {/* Structures Section */}
            <Paper sx={{ p: 1, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ pl: 2, pt: 2 }}>
                Structures
              </Typography>

              <List sx={{ m: 0, p: 0 }}>
                {customerDetails?.structures?.map((structure) => {
                  const IconName = structure.icon || 'DescriptionOutlined';
                  const IconComponent =
                    MuiIcons[IconName] || MuiIcons.DescriptionOutlined;
                  return (
                    <ListItem divider button key={structure.id}>
                      <ListItemIcon>
                        <IconComponent />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          structure.name_fr ||
                          structure.name_en ||
                          'Unnamed Structure'
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>

            {/* Modules Section */}
            <Paper sx={{ mb: 3, p: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ pl: 2, pt: 2 }}>
                Modules
              </Typography>
              <List sx={{ m: 0, p: 0 }}>
                {customerDetails?.modules?.map((module) => {
                  const IconName = module.icon || 'DescriptionOutlined';
                  const IconComponent =
                    MuiIcons[IconName] || MuiIcons.DescriptionOutlined;
                  return (
                    <ListItem divider button key={module.id}>
                      <ListItemIcon>
                        <IconComponent />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          module.name_fr ||
                          module.name_en ||
                          module.name ||
                          'Unnamed Module'
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Paper>

            {/* Blueprint Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="h6" gutterBottom>
                  Blueprint
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateBlueprint}
                  disabled={isSavingBlueprint}
                >
                  {blueprintData.structures.length > 0 ||
                  blueprintData.modules.length > 0 ||
                  blueprintData.extensions.length > 0
                    ? 'Edit Blueprint'
                    : 'Create New Blueprint'}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {blueprintError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {blueprintError}
                </Alert>
              )}
            </Paper>

            {/* Invoices Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Invoices
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {customerDetails?.invoices?.map((invoice) => (
                  <ListItem key={invoice.id}>
                    <ListItemText
                      primary={`Invoice #${invoice.id}`}
                      secondary={`${invoice.invoiceDate}`}
                    />
                    <ListItemText
                      primary={invoice?.finances?.subtotal}
                      secondary={invoice?.finances?.balance}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Contacts Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contacts
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                {customerDetails?.contacts?.map((contact) => (
                  <ListItem key={contact.id}>
                    <ListItemText
                      primary={contact.name}
                      secondary={`${contact.email} - ${contact.phone}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {/* Tax Information Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Usage
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    This month
                  </Typography>
                  <Typography variant="body1">{''}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Preferences Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Preferences
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Fields View
                  </Typography>
                  <Typography variant="body1">
                    {customerDetails?.preferences?.fieldsView || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Allow Node Access
                  </Typography>
                  <Typography variant="body1">
                    {customerDetails?.preferences?.allowNodeAccess
                      ? 'Yes'
                      : 'No'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Back Navigation
                  </Typography>
                  <Typography variant="body1">
                    {customerDetails?.preferences?.backNav
                      ? 'Enabled'
                      : 'Disabled'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Team Member Access
                  </Typography>
                  <Typography variant="body1">
                    {customerDetails?.preferences?.teamMember
                      ? 'Enabled'
                      : 'Disabled'}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <BlueprintEditor
        open={isBlueprintEditorOpen}
        onClose={() => setIsBlueprintEditorOpen(false)}
        onSave={handleSaveBlueprint}
        initialData={blueprintData}
        businessData={customer}
        blueprintId={bluePrintId}
        refreshData={fetchData}
      />
    </Box>
  );
}

export default CustomerDetailsPage;
